import { useEffect, useRef, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";
import { getToken, isMaster } from "../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: "auto",     label: "auto",     hint: "Sage picks the mode" },
  { id: "proposal", label: "proposal", hint: "real-world business doc, data-or-defer" },
  { id: "script",   label: "script",   hint: "scene / action / dialogue" },
  { id: "novella",  label: "novella",  hint: "long-form fiction" },
  { id: "lore",     label: "lore",     hint: "Sage canon (Oven, Ledger, Coffin Nails)" },
  { id: "essay",    label: "essay",    hint: "analysis, rigor, no invented citations" },
];

/**
 * QuillPage — the 5-mode author/editor desk. Any signed-in user pays HALF
 * of Sage-chat's real cost; master writes for free. New writers get a
 * $0.50 welcome credit (roughly 5 turns) on first touch.
 */
export default function QuillPage() {
  const [sessions, setSessions] = useState([]);
  const [works, setWorks] = useState([]);
  const [inkMeter, setInkMeter] = useState(null);
  const [wallet, setWallet] = useState(null);        // /api/quill/wallet payload
  const [me, setMe] = useState(null);                // /api/auth/me payload
  const [authChecked, setAuthChecked] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState("auto");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [openWork, setOpenWork] = useState(null);
  const scrollRef = useRef(null);

  // ── Helper: attach whichever auth the caller has ─────────────────────
  const authOpts = () => {
    const token = getToken();
    return token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : { credentials: "include" };
  };

  const reload = async () => {
    try {
      const [sr, wr, ir, wa] = await Promise.all([
        fetch(`${BACKEND_URL}/api/quill/sessions`, authOpts()),
        fetch(`${BACKEND_URL}/api/quill/works`, authOpts()),
        fetch(`${BACKEND_URL}/api/quill/ink-meter`),
        fetch(`${BACKEND_URL}/api/quill/wallet`, authOpts()),
      ]);
      if (sr.ok) setSessions((await sr.json()).sessions || []);
      if (wr.ok) setWorks((await wr.json()).works || []);
      if (ir.ok) setInkMeter(await ir.json());
      if (wa.ok) setWallet(await wa.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    // Every visitor loads the public ink meter.
    (async () => {
      try {
        const ir = await fetch(`${BACKEND_URL}/api/quill/ink-meter`);
        if (ir.ok) setInkMeter(await ir.json());
      } catch {}
    })();
    // Check auth: either master Bearer OR Google session cookie.
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (r.ok) setMe(await r.json());
      } catch {}
      setAuthChecked(true);
    })();
    if (isMaster()) reload();
  }, []);

  // The moment auth is known, load the writer's own state.
  useEffect(() => {
    if (me || isMaster()) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [streamText, messages]);

  const startLogin = () => {
    const redirectUrl = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loadSession = async (sid) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/quill/session/${sid}`, authOpts());
      if (r.ok) {
        const j = await r.json();
        setSessionId(sid);
        setMessages(j.messages || []);
        setStreamText("");
        setReceipt(null);
      }
    } catch (e) { toast.error(e.message); }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    setStreamText("");
    setReceipt(null);
  };

  const openWorkFull = async (wid) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/quill/works/${wid}`, authOpts());
      if (r.ok) setOpenWork(await r.json());
    } catch (e) { toast.error(e.message); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input.trim(), ts: new Date().toISOString(), mode };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);
    setStreamText("");
    setReceipt(null);
    const bodyText = input.trim();
    setInput("");
    try {
      const opts = authOpts();
      const r = await fetch(`${BACKEND_URL}/api/quill/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
        credentials: opts.credentials,
        body: JSON.stringify({ session_id: sessionId, message: bodyText, mode }),
      });
      if (r.status === 402) {
        toast.error("Wallet empty — top up before the next turn.");
        setStreaming(false);
        return;
      }
      if (!r.ok) throw new Error(await r.text());
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const eventMatch = chunk.match(/^event:\s*(.*)$/m);
          const dataMatch = chunk.match(/^data:\s*(.*)$/m);
          if (!eventMatch || !dataMatch) continue;
          const ev = eventMatch[1].trim();
          let d = {};
          try { d = JSON.parse(dataMatch[1]); } catch {}
          if (ev === "meta" && d.session_id && !sessionId) setSessionId(d.session_id);
          else if (ev === "token" && d.text) { acc += d.text; setStreamText(acc); }
          else if (ev === "receipt") setReceipt(d);
          else if (ev === "error") toast.error(d.message || "Quill error");
        }
      }
      // finalize turn into messages
      const assistantMsg = { role: "assistant", content: acc, ts: new Date().toISOString(), mode };
      setMessages((prev) => [...prev, assistantMsg]);
      setStreamText("");
      reload(); // pick up newly-filed works + wallet balance
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStreaming(false);
    }
  };

  const signedIn = !!me || isMaster();

  // ── Sign-in wall ─────────────────────────────────────────────────────
  if (authChecked && !signedIn) {
    return (
      <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
        <TopNav />
        <div className="mx-auto max-w-3xl px-4 md:px-8 py-16">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-sage-accent shrink-0">
              <ProductMark slug="quill" size={56} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-1">Quill · writer's desk</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">live · half of Sage-chat's cost per turn</div>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text mb-6">
            Half the price. All the honesty stack.
          </h1>
          <p className="text-sm text-sage-muted leading-relaxed sage-prose mb-6">
            Quill is the writer's desk. Every message costs half of what the same message costs on Sage-chat — the industry burns a hundred dollars to hand a chatbot a manuscript; Quill charges half. Sign in and Sage drops fifty cents in your wallet as a welcome — roughly five real turns on the house.
          </p>
          <div className="border border-sage-border p-4 space-y-3 text-sm">
            <div className="text-sage-muted">Five modes wait behind the door: <span className="text-sage-text">proposal · script · novella · lore · essay</span>. Filed works land on a private shelf only you can see. The public ledger shows the day's dollars burned — never a word of any draft.</div>
            <button
              onClick={startLogin}
              data-testid="quill-signin-google"
              className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
            >
              Sign in with Google →
            </button>
          </div>

          {/* The Founder's authorship offer — free Quill for authors with a vision. */}
          <div className="mt-6 border border-sage-accent p-4" data-testid="quill-authorship-teaser">
            <div className="text-xs uppercase tracking-[0.15em] text-sage-accent mb-2">For authors with a vision of a universe</div>
            <p className="text-sm text-sage-text sage-prose italic mb-3">
              "Are you an author with a vision of a universe? Send me your best two-page writing sample, and I will hand you my product for free, minus your cost and API bill."
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mb-3">— The Founder</p>
            <a
              href="/quill/authorship"
              data-testid="quill-authorship-link"
              className="inline-block text-xs uppercase tracking-[0.1em] border border-sage-border text-sage-muted px-3 py-2 hover:border-sage-accent hover:text-sage-text"
            >
              Apply for free Quill →
            </a>
          </div>
          {inkMeter && (
            <div className="mt-6 border border-sage-border p-4 font-mono text-xs text-sage-muted" data-testid="quill-ink-meter">
              public ink meter · turns: {inkMeter.turns} · in {inkMeter.chars_in?.toLocaleString?.() || 0} · out {inkMeter.chars_out?.toLocaleString?.() || 0} · ${(inkMeter.cost_usd || 0).toFixed(6)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── The desk ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
      <TopNav />
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted">Quill · writer's desk</div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text">Ink and honesty.</h1>
          </div>
          <div className="flex items-center gap-3">
            {wallet && wallet.role !== "master" && (
              <div className="border border-sage-accent px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-sage-accent tabular-nums" data-testid="quill-wallet-balance">
                wallet: ${(wallet.cash_usd || 0).toFixed(4)} · half rate
              </div>
            )}
            {wallet && wallet.role === "master" && (
              <div className="border border-sage-border px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-sage-muted" data-testid="quill-wallet-master">
                master · writes free
              </div>
            )}
            {inkMeter && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums" data-testid="quill-ink-meter">
                turns: {inkMeter.turns} · ${(inkMeter.cost_usd || 0).toFixed(6)}
              </div>
            )}
          </div>
        </div>

        {wallet && wallet.role !== "master" && wallet.turns_charged === 0 && (
          <div className="border border-sage-accent p-3 mb-4 text-xs text-sage-accent sage-prose" data-testid="quill-welcome-banner">
            Fifty cents on the house. Roughly five turns at half rate. When it runs out, top up on your Tiers page to keep writing.
          </div>
        )}

        {wallet && wallet.role !== "master" && wallet.cash_usd < 0.005 && wallet.turns_charged > 0 && (
          <div className="border border-sage-warn p-3 mb-4 text-xs text-sage-warn sage-prose" data-testid="quill-empty-banner">
            Wallet empty. Top up on your Tiers page before the next turn.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-4">
          {/* Sessions rail */}
          <aside className="border border-sage-border p-3" data-testid="quill-sessions-rail">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Sessions</div>
              <button onClick={newSession} data-testid="quill-new-session" className="text-[10px] uppercase text-sage-accent hover:underline">new</button>
            </div>
            <div className="space-y-1 max-h-[60vh] overflow-auto">
              {sessions.length === 0 && <div className="text-xs text-sage-faint">No sessions yet.</div>}
              {sessions.map((s) => (
                <button
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  className={`block w-full text-left text-xs px-2 py-1 border ${sessionId === s.session_id ? "border-sage-accent text-sage-text" : "border-sage-border text-sage-muted hover:border-sage-accent hover:text-sage-text"}`}
                  data-testid={`quill-session-${s.session_id}`}
                >
                  <div className="truncate">{s.session_id.slice(0, 12)}…</div>
                  <div className="text-[10px] text-sage-faint">{s.updated_at?.slice(0, 16)}</div>
                </button>
              ))}
            </div>
          </aside>

          {/* Chat */}
          <div className="border border-sage-border p-4 flex flex-col min-h-[70vh]" data-testid="quill-chat">
            <div className="flex flex-wrap gap-1 mb-3">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  title={m.hint}
                  data-testid={`quill-mode-${m.id}`}
                  className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${mode === m.id ? "border-sage-accent text-sage-accent" : "border-sage-border text-sage-muted hover:border-sage-text hover:text-sage-text"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-auto space-y-4 pr-2 mb-3">
              {messages.length === 0 && !streaming && (
                <div className="text-sm text-sage-faint italic">
                  Pick a mode above (or leave on auto), type an ask, and Quill goes. Say <span className="text-sage-accent">"save it"</span> when it's done and the piece lands on your shelf.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`text-sm leading-relaxed sage-prose ${m.role === "user" ? "text-sage-text" : "text-sage-muted"}`} data-testid={`quill-msg-${i}`}>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">
                    {m.role} · {m.mode}
                  </div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
              {streamText && (
                <div className="text-sm text-sage-muted leading-relaxed sage-prose" data-testid="quill-streaming">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mb-1">assistant · {mode} · streaming</div>
                  <div className="whitespace-pre-wrap">{streamText}<span className="inline-block w-2 h-4 bg-sage-accent animate-pulse ml-1"/></div>
                </div>
              )}
              {receipt && (
                <div className="border-t border-sage-border pt-3 mt-3 text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums" data-testid="quill-receipt">
                  {receipt.duration_ms/1000}s · in {receipt.input_tokens} · out {receipt.output_tokens} · real ${receipt.cost_usd?.toFixed(6)}
                  {receipt.charged_usd > 0 && (
                    <span className="text-sage-accent"> · charged ${receipt.charged_usd?.toFixed(6)} (half)</span>
                  )}
                  {typeof receipt.wallet_balance_usd === "number" && (
                    <span className="text-sage-muted"> · wallet ${receipt.wallet_balance_usd?.toFixed(4)}</span>
                  )}
                  {receipt.works_filed?.length > 0 && (
                    <div className="mt-1 text-sage-accent">filed: {receipt.works_filed.map(w => w.title).join(", ")}</div>
                  )}
                </div>
              )}
            </div>
            <form onSubmit={submit} className="flex gap-2">
              <textarea
                data-testid="quill-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                rows={3}
                placeholder={`Ask Quill in ${mode} mode…`}
                className="flex-1 bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent resize-y"
              />
              <button
                type="submit"
                data-testid="quill-submit"
                disabled={streaming || !input.trim()}
                className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40"
              >
                {streaming ? "Inking…" : "Send →"}
              </button>
            </form>
          </div>

          {/* Works library */}
          <aside className="border border-sage-border p-3" data-testid="quill-works-rail">
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-2">Filed works ({works.length})</div>
            <div className="space-y-2 max-h-[60vh] overflow-auto">
              {works.length === 0 && <div className="text-xs text-sage-faint">Nothing filed yet.</div>}
              {works.map((w) => (
                <button
                  key={w.work_id}
                  onClick={() => openWorkFull(w.work_id)}
                  className="block w-full text-left border border-sage-border hover:border-sage-accent p-2"
                  data-testid={`quill-work-${w.work_id}`}
                >
                  <div className="text-xs font-bold text-sage-text truncate">{w.title}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums mt-1">
                    {w.mode} · {w.chars?.toLocaleString?.() || 0} chars
                  </div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </div>

      {openWork && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setOpenWork(null)} data-testid="quill-work-modal">
          <div className="bg-sage-bg border border-sage-border max-w-3xl w-full max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint">{openWork.mode} · {openWork.chars?.toLocaleString?.() || 0} chars</div>
                <div className="text-xl font-bold text-sage-text">{openWork.title}</div>
              </div>
              <button onClick={() => setOpenWork(null)} className="text-xs text-sage-faint hover:text-sage-text">close ×</button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-sage-muted leading-relaxed sage-prose">{openWork.content}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
